import { useEffect, useState } from "react";
import ProctoringPOC from "@/Components/ProfileLandmarkCapture";
import { useAuthStore } from "@/contexts/AuthContext";
import { saveProfileLandmarks } from "@/services/profileLandmarks";
import PermissionScreen from "@/Components/roleplay/components/PermissionScreen";

/**
 * Requires authenticated users to capture profile landmarks when enabled by their organization.
 *
 * @returns The landmark capture interface, a camera permission screen, or `null` when capture is unnecessary.
 */
export default function ProfileLandmarkGate() {
  const { user, org, isAuthenticated, isLoading, refreshUser } = useAuthStore();
  const [required, setRequired] = useState(false);
  const [isCameraPermissionDenied, setIsCameraPermissionDenied] =
    useState(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user?.userId) return;

    let cancelled = false;
    async function checkRequirement() {
      const hasLandmarks =
        !!user?.profileLandmarks &&
        Object.keys(user.profileLandmarks).length > 0;
      const requiresLandmarks =
        org?.enableProfileLandmarks === true && !hasLandmarks;

      if (cancelled || !requiresLandmarks) {
        if (!cancelled) {
          setRequired(false);
          setIsCameraPermissionDenied(false);
        }
        return;
      }

      try {
        const permission = await navigator.permissions.query({
          name: "camera",
        });
        if (!cancelled && permission.state === "denied") {
          setIsCameraPermissionDenied(true);
        }
      } catch {
        // Permission Status API is unavailable; the capture screen will request access.
      }

      if (!cancelled) setRequired(true);
    }

    void checkRequirement();

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    isLoading,
    user?.userId,
    user?.profileLandmarks,
    org?.enableProfileLandmarks,
  ]);

  useEffect(() => {
    if (!required || isCameraPermissionDenied) return;

    let cancelled = false;
    let removePermissionListener: (() => void) | undefined;

    async function watchCameraPermission() {
      try {
        const permission = await navigator.permissions.query({
          name: "camera",
        });
        if (cancelled) return;

        const handlePermissionChange = () => {
          if (permission.state === "denied") {
            setIsCameraPermissionDenied(true);
          }
        };

        handlePermissionChange();
        permission.addEventListener("change", handlePermissionChange);
        removePermissionListener = () =>
          permission.removeEventListener("change", handlePermissionChange);
      } catch {
        // Permission Status API is unavailable; the capture screen will request access.
      }
    }

    void watchCameraPermission();

    return () => {
      cancelled = true;
      removePermissionListener?.();
    };
  }, [isCameraPermissionDenied, required]);

  if (!required) return null;

  if (isCameraPermissionDenied) {
    return (
      <div className="fixed inset-0 z-[101] flex min-h-dvh flex-col overflow-y-auto bg-appBg">
        <PermissionScreen isInterview={false} />
      </div>
    );
  }

  return (
    <ProctoringPOC
      required
      onCameraUnavailable={() => setIsCameraPermissionDenied(true)}
      onComplete={async ([left, straight, right]) => {
        await saveProfileLandmarks([left, straight, right]);
        await refreshUser();
        setRequired(false);
      }}
    />
  );
}
